import React from "react";
import { Table, Image, Tag, Button, Space, Tooltip } from "antd";
import { useQuery } from "@tanstack/react-query";
import { getCourses } from "./api";
import { EditOutlined, BookOutlined } from "@ant-design/icons";

interface CoursesTableProps {
  onEditCourse: (courseId: number) => void;
  onViewLessons: (courseId: number) => void;
}

export default function CoursesTable({ onEditCourse, onViewLessons }: CoursesTableProps) {
  const { data, isLoading, error } = useQuery({ queryKey: ["courses"], queryFn: getCourses });

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60, fixed: 'left' as const },
    { 
      title: "Название", 
      dataIndex: "title", 
      key: "title",
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: "Короткое описание",
      dataIndex: "shortDescription",
      key: "shortDescription",
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text || '-'}>
          <div style={{ maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text || '-'}
          </div>
        </Tooltip>
      )
    },
    { 
      title: "Описание", 
      dataIndex: "description", 
      key: "description",
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text || '-'}>
          <div style={{ maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text || '-'}
          </div>
        </Tooltip>
      )
    },
    {
      title: "Картинка",
      dataIndex: "image_url",
      key: "image_url",
      width: 80,
      render: (url: string) => url ? <Image width={50} src={url} /> : '-',
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) =>
        status === "published" ? (
          <Tag color="green">Опубликован</Tag>
        ) : (
          <Tag color="orange">Черновик</Tag>
        ),
    },
    {
      title: "AI-ассистент",
      dataIndex: "aiAssistant",
      key: "aiAssistant",
      width: 120,
      render: (aiAssistant: string) => {
        if (aiAssistant === 'claude') {
          return <Tag color="purple">Claude</Tag>;
        } else {
          return <Tag color="blue">GPT</Tag>;
        }
      },
    },
    {
      title: "Тип курса",
      key: "courseType",
      width: 120,
      render: (record: any) => {
        if (record.isPaid) {
          return (
            <div>
              <Tag color="gold">💰 Платный</Tag>
              {record.trialLessonsCount > 0 && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  🆓 {record.trialLessonsCount} уроков бесплатно
                </div>
              )}
            </div>
          );
        } else {
          return <Tag color="green">🆓 Бесплатный</Tag>;
        }
      },
    },
    {
      title: "Лимит AI",
      dataIndex: "dailyAiMessageLimit",
      key: "dailyAiMessageLimit",
      width: 100,
      render: (limit: number) => (
        <Tooltip title="Дневной лимит AI сообщений">
          <Tag color="cyan">{limit || 30}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Лимит тестов",
      dataIndex: "dailyTestLimit",
      key: "dailyTestLimit",
      width: 100,
      render: (limit: number) => (
        <Tooltip title="Дневной лимит неудачных попыток тестов">
          <Tag color="orange">{limit || 10}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Уроки",
      dataIndex: "lessons",
      key: "lessons",
      width: 80,
      render: (lessons: any[]) => (
        <Tag color="blue">{lessons?.length || 0}</Tag>
      ),
    },
    { 
      title: "Создан", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 120,
      render: (date: string) => formatDate(date)
    },
    { 
      title: "Обновлён", 
      dataIndex: "updatedAt", 
      key: "updatedAt",
      width: 120,
      render: (date: string) => formatDate(date)
    },
    {
      title: "Действия",
      key: "actions",
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title={`Просмотр уроков (${record.lessons?.length || 0})`}>
            <Button
              type="default"
              icon={<BookOutlined />}
              onClick={() => onViewLessons(record.id)}
              size="small"
            >
              Уроки
            </Button>
          </Tooltip>
          <Tooltip title="Редактировать курс">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEditCourse(record.id)}
              size="small"
            >
              Изменить
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) return <div>Загрузка...</div>;
  if (error) return <div>Ошибка: {(error as Error).message}</div>;

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      scroll={{ x: 1400 }}
      size="small"
    />
  );
} 